"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause } from "lucide-react";
import type { HeroCarouselComponent, HeroCarouselSlide } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, resolveImageValue } from "../../utils/imageOptions";

const DEFAULT_AUTOPLAY_SECONDS = 9;
/** Cross-fade duration between slides (ms) — mirrored in the CSS below. */
const FADE_MS = 1200;

/**
 * Animated hero carousel for the home page (client request 07/2026: replace the
 * static banner with an "animatique" carousel usable as « info à la une », and
 * showcase the banknotes and the head-office imagery).
 *
 * Design rules (carousel UX best practices):
 * - 3-5 slides, cross-fade (no lateral push), subtle Ken Burns zoom on the image;
 * - autoplay (default 7 s) runs continuously (no hover pause — demande client);
 *   clickable progress bars show where you are and how long the slide has left;
 * - keyboard ←/→, touch swipe, `role="region"` + `aria-roledescription`;
 * - `prefers-reduced-motion` disables both the zoom and the autoplay;
 * - only the first slide loads eagerly (LCP), the rest are lazy.
 *
 * No carousel library — the SDK has none and this stays dependency-free (CSS
 * keyframes + React state, same client-component pattern as ContactFormRenderer).
 */
export function HeroCarouselRenderer({ component }: { component: HeroCarouselComponent }) {
  const slides = useMemo(
    () => (component.content.slides ?? []).filter((s) => (s?.title ?? "").trim() !== ""),
    [component.content.slides],
  );
  const autoplaySeconds = component.content.autoplaySeconds ?? DEFAULT_AUTOPLAY_SECONDS;

  const [index, setIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  // Verrou du défilement (WCAG 2.2.2, spéc client) : choisir une diapositive (barre de
  // progression ou flèche) VERROUILLE l'autoplay ; on ne déverrouille qu'en recliquant
  // la même diapositive ou la surface du bandeau (hors liens/boutons). Un indicateur
  // discret superposé signale l'état verrouillé sans rien déplacer.
  const [paused, setPaused] = useState(false);
  // Bumped on every manual navigation so the active progress bar restarts its
  // fill animation even when the slide index happens to stay the same.
  const [cycle, setCycle] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const goTo = useCallback(
    (next: number) => {
      if (slides.length === 0) return;
      setIndex(((next % slides.length) + slides.length) % slides.length);
      setCycle((c) => c + 1);
    },
    [slides.length],
  );

  const autoplayActive = !reducedMotion && !paused && autoplaySeconds > 0 && slides.length > 1;

  useEffect(() => {
    if (!autoplayActive) return;
    const t = window.setTimeout(() => goTo(index + 1), autoplaySeconds * 1000);
    return () => window.clearTimeout(t);
  }, [autoplayActive, autoplaySeconds, index, cycle, goTo]);

  if (slides.length === 0) return null;
  const showControls = slides.length > 1;

  return (
    <section
      className="group/hero relative z-0 isolate w-full overflow-hidden bg-[#0B2F68] text-white"
      role="region"
      aria-roledescription="carrousel"
      aria-label="À la une de la Banque Centrale du Congo"
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") goTo(index - 1);
        if (e.key === "ArrowRight") goTo(index + 1);
      }}
      // Pause au clic (WCAG 2.2.2, demande client : pas de bouton visible) : un clic sur
      // la surface du carrousel stoppe le défilement, un second le relance. Les clics
      // sur les liens, flèches et barres de progression restent intacts.
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a, button")) return;
        setPaused((p) => !p);
      }}
      onTouchStart={(e) => { touchStartX.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        const start = touchStartX.current;
        touchStartX.current = null;
        if (start === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? start) - start;
        if (Math.abs(dx) > 48) goTo(index + (dx < 0 ? 1 : -1));
      }}
    >
      <style>{`
        @keyframes bccHeroKenBurns { from { transform: scale(1); } to { transform: scale(1.06); } }
        @keyframes bccHeroProgress { from { width: 0%; } to { width: 100%; } }
        @keyframes bccHeroTextIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Hauteur mobile resserrée (plage 320→380px, revue client 02/08 ×2) : titre
          réduit + clamp 3 lignes + boutons une ligne = le bloc tient sans réserve
          de hauteur.
          À partir de `md`, la hauteur est FIXE. Elle suivait la hauteur de la
          fenêtre (`38vh`, plancher 350px) : sur une tablette dont la fenêtre est
          courte, le bandeau se resserrait de 430 à 350px, et le même appareil
          montrait deux carrousels différents selon la barre d'outils du
          navigateur. Une mesure de largeur (le mobile) est stable ; une mesure de
          hauteur ne l'est pas. */}
      <div className="relative h-[clamp(320px,90vw,380px)] md:h-[430px]">
        {slides.map((slide, i) => (
          <Slide
            key={i}
            slide={slide}
            active={i === index}
            first={i === 0}
            kenBurns={!reducedMotion}
            frozen={paused}
            durationSeconds={autoplaySeconds}
          />
        ))}

        {/* Contenu du slide actif — remonté au-dessus des couches image. */}
        {/* Mobile : bloc entièrement centré sur voile bleu dominant ;
            desktop : centré verticalement, aligné à gauche sur le conteneur. */}
        <div className="pointer-events-none absolute inset-0 z-10 mx-auto flex max-w-7xl flex-col items-center justify-center px-4 md:items-start md:px-8 lg:px-16">
          {/* Keyé sur l'index SEUL : l'animation d'entrée du texte ne rejoue que
              quand la diapositive change réellement — pas à chaque resynchro de
              la barre (sortie de survol, reprise), qui bump `cycle`. */}
          <SlideText key={index} slide={slides[index]} reducedMotion={reducedMotion} />
        </div>

        {/* Indicateur d'état verrouillé — SURCOUCHE discrète en haut à droite,
            pointer-events-none et absolue : elle ne déplace rien d'existant. */}
        {paused && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-4 top-4 z-30 grid h-7 w-7 place-items-center rounded-full bg-black/30 text-white/90 backdrop-blur-sm md:right-6 md:top-5"
          >
            <Pause className="h-3.5 w-3.5" aria-hidden />
          </span>
        )}

        {showControls ? (
          <>
            {/* Barres de progression cliquables. La pause se fait par un clic sur la
                surface du carrousel (onClick de la section), pas par un bouton. */}
            <div className="absolute inset-x-0 bottom-0 z-20">
              <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 pb-5 md:px-8 lg:px-16">
                {slides.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      // Recliquer la diapositive ACTIVE déverrouille ; en choisir une
                      // (même logique pour une nouvelle) verrouille l'autoplay.
                      if (i === index && paused) {
                        setPaused(false);
                        setCycle((c) => c + 1);
                      } else {
                        goTo(i);
                        setPaused(true);
                      }
                    }}
                    aria-label={`Diapositive ${i + 1} : ${s.title}`}
                    aria-current={i === index}
                    className="group relative h-6 flex-1 cursor-pointer lg:max-w-[6rem]"
                  >
                    <span className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 overflow-hidden rounded-full bg-white/30 transition group-hover:bg-white/45">
                      <span
                        className="block h-full rounded-full bg-[#FFA401]"
                        style={
                          i === index
                            ? reducedMotion || slides.length < 2 || paused
                              ? // Verrouillé (ou sans autoplay) : arrêt instantané, segment
                                // actif PLEIN en ambre — pas de remplissage grisé figé.
                                { width: "100%" }
                              : { animation: `bccHeroProgress ${autoplaySeconds}s linear forwards` }
                            : { width: i < index ? "100%" : "0%", opacity: i < index ? 0.45 : 1 }
                        }
                        key={i === index ? cycle : undefined}
                      />
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Flèches — discrètes, révélées au survol sur desktop. */}
            <button
              type="button"
              onClick={() => { goTo(index - 1); setPaused(true); }}
              aria-label="Diapositive précédente"
              className="absolute left-3 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white opacity-0 backdrop-blur-sm transition hover:bg-white/25 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:grid group-hover/hero:opacity-100"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => { goTo(index + 1); setPaused(true); }}
              aria-label="Diapositive suivante"
              className="absolute right-3 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white opacity-0 backdrop-blur-sm transition hover:bg-white/25 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:grid group-hover/hero:opacity-100"
            >
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </>
        ) : null}
      </div>
    </section>
  );
}

/** Image layer of one slide — always mounted so the cross-fade is seamless. */
function Slide({ slide, active, first, kenBurns, frozen, durationSeconds }: {
  slide: HeroCarouselSlide;
  active: boolean;
  first: boolean;
  kenBurns: boolean;
  /** Verrou actif : gèle le zoom Ken Burns SUR PLACE, instantanément. */
  frozen: boolean;
  durationSeconds: number;
}) {
  const { src, options } = resolveImageValue(slide.imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  return (
    <div
      aria-hidden={!active}
      className="absolute inset-0"
      style={{ opacity: active ? 1 : 0, transition: `opacity ${FADE_MS}ms ease-in-out`, zIndex: active ? 1 : 0 }}
    >
      <div
        className="absolute inset-0"
        style={
          active && kenBurns
            ? {
                animation: `bccHeroKenBurns ${Math.max(durationSeconds, 5) + 1.2}s ease-out forwards`,
                animationPlayState: frozen ? "paused" : "running",
              }
            : kenBurns
              ? // Diapo sortante : rester figée à l'état FINAL du zoom pendant le
                // fondu croisé — sans ça le transform retombait d'un coup à
                // scale(1) au moment où l'animation était retirée.
                { transform: "scale(1.06)" }
              : undefined
        }
      >
        <RuntimeImage
          src={src}
          alt={slide.imageAlt || slide.title}
          className="absolute inset-0 h-full w-full object-cover saturate-[0.72] contrast-[1.04]"
          style={objectPositionStyle}
          priority={first}
          loading={first ? undefined : "lazy"}
        />
      </div>
      {/* Voile institutionnel : dégradé horizontal sur desktop (texte à gauche,
          image à droite) ; voile uniforme plus soutenu sur mobile où le texte
          occupe toute la largeur. */}
      <div
        className="absolute inset-0 hidden md:block"
        style={{ background: "linear-gradient(90deg, rgba(7,34,88,0.97) 0%, rgba(8,45,105,0.93) 38%, rgba(9,55,119,0.69) 66%, rgba(8,39,91,0.32) 100%)" }}
      />
      <div
        className="absolute inset-0 md:hidden"
        style={{ background: "linear-gradient(180deg, rgba(7,34,88,0.88) 0%, rgba(8,45,105,0.94) 70%, rgba(7,34,88,0.98) 100%)" }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-24"
        style={{ background: "linear-gradient(180deg, transparent, rgba(7,31,82,0.55))" }}
      />
    </div>
  );
}

/** Text block of the active slide — remounted on each change for the entrance animation. */
/** French typography: high punctuation (: ; ! ?) must never start a line. */
function frenchNbsp(text: string): string {
  return text.replace(/ ([:;!?\u00BB])/g, "\u00A0$1");
}

function SlideText({ slide, reducedMotion }: { slide: HeroCarouselSlide; reducedMotion: boolean }) {
  const cta = resolveLinkHref(slide.ctaHref);
  const secondary = resolveLinkHref(slide.secondaryHref);
  const anim = (delayMs: number) =>
    reducedMotion ? undefined : { animation: `bccHeroTextIn 600ms ${delayMs}ms cubic-bezier(0.2, 0.7, 0.3, 1) both` };
  return (
    // Gouttière mobile resserrée (le conteneur externe garde déjà 16px) : avec le
    // titre remonté au niveau des pages de niveau 2, chaque caractère de largeur
    // compte — 24px cumulés par côté au lieu de 40.
    <div className="pointer-events-auto max-w-2xl px-2 pt-4 pb-12 text-center md:px-0 md:pt-6 md:pb-16 md:text-left">
      {slide.eyebrow ? (
        <p
          className="mb-3 flex items-center justify-center gap-3 text-xs font-bold uppercase tracking-[0.18em] text-[#FFA401] md:justify-start md:text-sm"
          style={anim(0)}
        >
          <span className="h-[2px] w-8 rounded-full bg-[#FFA401]" aria-hidden />
          {slide.eyebrow}
          <span className="h-[2px] w-8 rounded-full bg-[#FFA401] md:hidden" aria-hidden />
        </p>
      ) : null}
      {/* Mobile : un cran SOUS les pages de niveau 2 (revue client 02/08) — un titre
          long comme « Apprenez à reconnaître les billets » tombait sur 4 lignes à
          2.25rem. 1.9rem tient en 2-3 lignes, et le clamp à 3 lignes pose une
          ellipsis dans le pire des cas. Desktop inchangé (pointe à 3.25rem). */}
      <h2
        className="mx-auto max-w-[22ch] text-[1.9rem] font-bold leading-[1.14] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3] overflow-hidden sm:text-[2.2rem] md:mx-0 md:max-w-none md:text-[2.7rem] md:leading-tight lg:text-[2.8rem] xl:text-[3.25rem] xl:leading-[1.08]"
        style={{ fontFamily: "var(--font-aleo)", textWrap: "balance", ...anim(90) }}
      >
        {frenchNbsp(slide.title)}
      </h2>
      {slide.body ? (
        <p
          className="mx-auto mt-3 max-w-[46ch] text-[0.85rem] leading-relaxed text-white/90 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden sm:text-base md:mx-0 md:max-w-xl md:text-xl lg:text-lg"
          style={{ fontFamily: "Nunito Sans", ...anim(180) }}
        >
          {frenchNbsp(slide.body)}
        </p>
      ) : null}
      {cta || secondary ? (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3 md:justify-start" style={anim(260)}>
          {/* Libellés de bouton bornés à UNE ligne (revue client 02/08) : la pilule
              garde sa forme, le texte trop long s'ellipse au lieu de casser sur deux
              lignes — max-w-full pour que le truncate ait une borne dans le flex. */}
          {cta && slide.ctaLabel ? (
            <RuntimeLink
              href={cta}
              className="inline-flex max-w-full items-center gap-2 rounded-full bg-[#FFA401] px-6 py-2.5 text-base font-semibold text-white transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:px-7 md:py-3 md:text-lg"
            >
              <span className="truncate">{slide.ctaLabel}</span>
              <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
            </RuntimeLink>
          ) : null}
          {secondary && slide.secondaryLabel ? (
            <RuntimeLink
              href={secondary}
              className="hidden max-w-full items-center rounded-full border border-white/45 px-5 py-2.5 text-base font-semibold text-white transition hover:border-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:px-6 md:py-3 md:text-lg lg:inline-flex"
            >
              <span className="truncate">{slide.secondaryLabel}</span>
            </RuntimeLink>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
